import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import { useTranslation, Trans } from 'react-i18next';
import {
  listCompanyLicenses,
  listCompanyUsers,
  assignCompanyLicense,
  unassignCompanyLicense,
  readAxiosDetail,
  type CompanyLicense,
  type CompanyUser,
} from './api_calls';

import { Icon } from '@liftango/ui-icons';
interface Props {
  onLicenseChange?: () => void;
}

export default function CompanyLicensesTab({ onLicenseChange }: Props) {
  const { t } = useTranslation('company');

  const getLicenseStatus = (expiresAt: string): { label: string; color: 'success' | 'warning' | 'error' | 'default' } => {
    const year = new Date(expiresAt).getFullYear();
    if (year >= 9999) return { label: t('licenses.status.neverExpires'), color: 'default' };
    const now = Date.now();
    const exp = new Date(expiresAt).getTime();
    if (exp < now) return { label: t('licenses.status.expired'), color: 'error' };
    const daysLeft = (exp - now) / (1000 * 60 * 60 * 24);
    if (daysLeft < 30) return { label: t('licenses.status.expiringSoon'), color: 'warning' };
    return { label: t('licenses.status.active'), color: 'success' };
  };

  const formatExpiry = (expiresAt: string): string => {
    const year = new Date(expiresAt).getFullYear();
    if (year >= 9999) return t('licenses.expiry.never');
    return new Date(expiresAt).toLocaleDateString();
  };

  const [licenses, setLicenses] = useState<CompanyLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Assign dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignUsers, setAssignUsers] = useState<CompanyUser[]>([]);
  const [assignMetaLoading, setAssignMetaLoading] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [saving, setSaving] = useState(false);

  // Unassign dialog
  const [unassignTarget, setUnassignTarget] = useState<CompanyLicense | null>(null);
  const [unassignLoading, setUnassignLoading] = useState(false);

  const load = async () => {
    try {
      const data = await listCompanyLicenses();
      setLicenses(data);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const assignedLicenses = licenses.filter((l) => l.user_id);
  const availableCount = licenses.filter((l) => !l.user_id && new Date(l.expires_at).getTime() > Date.now()).length;

  const openAssign = async () => {
    setError(null);
    setAssignOpen(true);
    setAssignMetaLoading(true);
    try {
      const users = await listCompanyUsers();
      const licensedUserIds = new Set(assignedLicenses.map((l) => l.user_id));
      const unlicensed = users.filter((u) => !licensedUserIds.has(u.id));
      setAssignUsers(unlicensed);
      if (unlicensed[0]) setAssignUserId(unlicensed[0].id);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setAssignMetaLoading(false);
    }
  };

  const handleAssign = async () => {
    setSaving(true);
    try {
      await assignCompanyLicense({ user_id: assignUserId });
      setAssignOpen(false);
      await load();
      onLicenseChange?.();
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async () => {
    if (!unassignTarget) return;
    setUnassignLoading(true);
    try {
      await unassignCompanyLicense(unassignTarget.license_id);
      setUnassignTarget(null);
      await load();
      onLicenseChange?.();
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setUnassignLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('licenses.summary', { assigned: assignedLicenses.length, total: licenses.length, available: availableCount })}
        </Typography>
        <Button
          startIcon={<Icon size={20} name="plus" />}
          variant="contained"
          size="small"
          disabled={availableCount === 0}
          onClick={() => void openAssign()}
        >
          {t('licenses.assignLicense')}
        </Button>
      </Box>

      {assignedLicenses.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {licenses.length === 0
            ? t('licenses.noLicenses')
            : t('licenses.noneAssigned')}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('licenses.columns.user')}</TableCell>
              <TableCell>{t('licenses.columns.expires')}</TableCell>
              <TableCell>{t('licenses.columns.status')}</TableCell>
              <TableCell>{t('licenses.columns.granted')}</TableCell>
              <TableCell align="right">{t('licenses.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignedLicenses.map((lic) => {
              const status = getLicenseStatus(lic.expires_at);
              return (
                <TableRow key={lic.license_id}>
                  <TableCell>{lic.user_email}</TableCell>
                  <TableCell>{formatExpiry(lic.expires_at)}</TableCell>
                  <TableCell>
                    <Chip label={status.label} size="small" color={status.color} variant="outlined" />
                  </TableCell>
                  <TableCell>{new Date(lic.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" onClick={() => setUnassignTarget(lic)}>
                      {t('licenses.actions.unassign')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Assign license dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('licenses.assignDialog.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {assignMetaLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : assignUsers.length === 0 ? (
            <Typography color="text.secondary">
              {t('licenses.assignDialog.allUsersHaveLicense')}
            </Typography>
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel>{t('licenses.assignDialog.userLabel')}</InputLabel>
              <Select value={assignUserId} label={t('licenses.assignDialog.userLabel')} onChange={(e) => setAssignUserId(e.target.value)}>
                {assignUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} disabled={saving}>{t('licenses.assignDialog.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void handleAssign()}
            disabled={saving || assignMetaLoading || assignUsers.length === 0}
          >
            {saving ? <CircularProgress size={18} /> : t('licenses.assignDialog.assign')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unassign confirmation */}
      <Dialog open={!!unassignTarget} onClose={() => setUnassignTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('licenses.unassignDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            <Trans
              i18nKey="licenses.unassignDialog.message"
              t={t}
              values={{ email: unassignTarget?.user_email ?? '' }}
              components={{ bold: <strong /> }}
            />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnassignTarget(null)} disabled={unassignLoading}>{t('licenses.unassignDialog.cancel')}</Button>
          <Button variant="contained" color="error" onClick={() => void handleUnassign()} disabled={unassignLoading}>
            {unassignLoading ? <CircularProgress size={18} /> : t('licenses.unassignDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
